// Pure formatting helpers. No React, no I/O, so they unit-test in isolation.

/**
 * Count with a pluralized noun: pluralize(1, 'order') -> "1 order", pluralize(2, 'order') ->
 * "2 orders". Pass an explicit plural for irregular words (pluralize(2, 'person', 'people')).
 */
export function pluralize(n: number, singular: string, plural?: string): string {
  const word = n === 1 ? singular : (plural ?? `${singular}s`);
  return `${n} ${word}`;
}

/** Integer cents to a plain dollar string. 5800 -> "$58.00", -6200 -> "-$62.00". */
export function money(cents: number): string {
  const neg = cents < 0;
  const abs = Math.abs(cents);
  const s = `$${(abs / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return neg ? `-${s}` : s;
}

/** A short date from an ISO string. "2026-10-02T..." -> "Oct 2, 2026". Empty for null. */
export function shortDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Format a DATE-ONLY value, never shifted by timezone. Payout, hold-clear and batch dates
 * are calendar dates, not instants: a Nov 1 batch must read "Nov 1" in every timezone.
 * Accepts "YYYY-MM-DD" (what the API now returns) or a full ISO timestamp (whose leading
 * UTC calendar date is the intended day). Unlike shortDate it never builds a Date, so it
 * cannot render Oct 31 for a Nov 1 value in Mountain or Hawaii time. Empty for null/junk.
 */
export function dateOnly(value: string | null | undefined): string {
  if (!value) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!m) return '';
  const month = MONTHS[Number(m[2]) - 1];
  if (!month) return '';
  return `${month} ${Number(m[3])}, ${m[1]}`;
}

/** A whole-number percent. 0.1667 -> "17%". */
export function percent(fraction: number): string {
  return `${Math.round(fraction * 100)}%`;
}

/** YYYY-MM month key to a readable label. "2026-10" -> "Oct 2026". */
export function monthLabel(key: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(key);
  if (!m) return key;
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = names[Number(m[2]) - 1] ?? m[2];
  return `${month} ${m[1]}`;
}

/** Days since an ISO date relative to now, or null if no date. */
export function daysAgo(iso: string | null | undefined, now: Date = new Date()): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((now.getTime() - d.getTime()) / (24 * 60 * 60 * 1000));
}

/** "Active 3 days ago" style relative line, warm and plain. */
export function lastSeen(iso: string | null | undefined, now: Date = new Date()): string {
  const n = daysAgo(iso, now);
  if (n == null) return 'No activity yet';
  if (n <= 0) return 'Active today';
  if (n === 1) return 'Active yesterday';
  return `Active ${n} days ago`;
}
